import { HttpErrorResponse } from "@angular/common/http";
import { environment } from "environments/environment";
import { v4 as uuidv4, parse as uuidParse } from 'uuid';
/** Model: Common */
export class ApiError extends Error {
    public code: number = 0;
    public description: string | null = null;
    public codeR: string | null = null;
    public httpErrorResponse: HttpErrorResponse | null = null;
    constructor(message: string, code: number, description: string, codeR: string, httpErrorResponse: HttpErrorResponse | null) {
        super(message);
        this.code = code;
        this.description = description;
        this.codeR = codeR;
        this.httpErrorResponse = httpErrorResponse;
    }

}

/** Model: Common */
export class Address {
    street?: string;
    city?: string;
    country?: string;
    postcode?: string;
    state?: string;
    venue?: string;
    latitude?: string;
    longitude?: string;

    static parse(data: any): Address | null {
        if (!data) {
            return null;
        }

        const obj = new Address();
        obj.street = data.street;
        obj.city = data.city;
        obj.country = data.country;
        obj.postcode = data.postcode;
        obj.state = data.state;
        obj.venue = data.venue;
        obj.latitude = data.latitude;
        obj.longitude = data.longitude;
        return obj;
    }

}

export class Paging {
    constructor(
        public count: number,
        public cur_page: number,
        public page_size: number,
        public total_page: number,
        public next_page_num: number,
        public prev_page_num: number,
        public next: string | null,
        public prev: string | null,
    ) { }

    static parse(item: any): Paging {
        return new Paging(
            item.count,
            item.cur_page,
            item.page_size,
            item.total_page,
            item.next_page_num,
            item.prev_page_num,
            item.next,
            item.prev,
        );
    }

    static parse_db(item: any): Paging {
        return new Paging(
            item.count,
            item.cur_page,
            item.page_size,
            item.total_page,
            item.next_page_num,
            item.prev_page_num,
            item.next,
            item.prev,
        );
    }
}

export abstract class RawJsonDict {
    rawJson: any;

    constructor(data: any) {
        this.rawJson = data;
    }
}

export abstract class ContactFunction extends RawJsonDict {
    contacts: any;

    constructor(data: any) {
        super(data);
    }

    getContactPhone(type: string): string | null | undefined {
        try {
            return this.contacts['phones'][type];
        } catch (error) {
            return null;
        }
    }

    getContactEmail(type: string): string | null | undefined {
        try {
            return this.contacts['emails'][type];
        } catch (error) {
            return null;
        }
    }

    getContactSocial(key: string) {
        try {
            return this.contacts['socials'][key];
        } catch (error) {

        }
        return '';
    }
}

/** Model: The App */
export class TheApp extends RawJsonDict {
    id: number;
    uuid: string;
    title: string;
    meta: any;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): TheApp | null {
        if (!data) {
            return null;
        }

        const instance = new TheApp(data);
        instance.id = data.id;
        instance.uuid = data.uuid;
        instance.title = data.title;
        instance.meta = data.meta ?? {};

        return instance;
    }
}

/** Model: Tag */
export class Tag {
    id: number;
    uuid: string;
    name: string;
    type: string;
    meta: any;
    created?: Date;
    lastModified?: Date;
    memberCount: number;

    static parse(data: any): Tag | null {
        if (!data) {
            return null;
        }

        const instance = new Tag();
        instance.id = data.id;
        instance.uuid = data.uuid;
        instance.name = data.name;
        instance.type = data.type;
        instance.memberCount = data.member_count ?? 0;
        instance.meta = data.meta ?? {};

        if (data.created) {
            try {
                instance.created = new Date(data.created);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.last_modified) {
            try {
                instance.lastModified = new Date(data.last_modified);
            } catch (error) {
                // Invalid date format
            }
        }

        return instance;
    }
}

/** Model: Industry */
export class Industry {
    id: number;
    uuid: string;
    title: string;
    description: string;
    meta: any;
    parent?: Industry | null;

    static parse(data: any): Industry | null {
        if (!data) {
            return null;
        }

        const instance = new Industry();
        instance.id = data.id;
        instance.uuid = data.uuid;
        instance.title = data.title;
        instance.description = data.description;
        instance.meta = data.meta ?? {};

        if (data.parent) {
            instance.parent = Industry.parse(data.parent);
        }

        return instance;
    }

    displayName(): string {
        const names: string[] = [];
        if (this.parent) {
            names.push(this.parent.title);
        }
        names.push(this.title);

        return names.join(' - ');
    }
}

/** Model: User */
export class User extends RawJsonDict {
    id: number;
    firstName: string;
    lastName: string;
    username: string;
    email: string;
    profile?: Profile;
    teamRoles: Team[] = [];

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): User | null {
        if (!data) {
            return null;
        }

        const obj = new User(data);
        obj.id = data.id;
        obj.firstName = data.first_name;
        obj.lastName = data.last_name;
        obj.email = data.email;
        obj.username = data.username;

        if (data.profile) {
            obj.profile = Profile.parse(data.profile);
        }

        if (data.roles) {
            obj.teamRoles = data.roles.map((x: any) => Team.parse(x)).filter(Boolean);
        }

        return obj;
    }

    getName(): string {
        const names: string[] = [];
        if (this.firstName) {
            names.push(this.firstName);
        }

        if (this.lastName) {
            names.push(this.lastName);
        }

        return names.join(' ');
    }

    get avatar(): string | null {
        if (this.profile?.avatarPhoto) {
            return this.profile.avatarPhoto.file;
        }

        return null;
    }

}

export class Profile extends ContactFunction {
    mobile?: string;
    dob?: string;
    bio?: string;
    gender?: string;
    address?: Address | null;
    workInfo: any;
    meta: any;
    avatarPhoto?: MediaObject | null;
    coverPhoto?: MediaObject | null;

    constructor(data: any = null) {
        super(data);
    }

    needLinkFirebase(): boolean {
        return this.meta?.['need_ext_user'] ?? false;
    }

    static parse(data: any): Profile | null {
        if (!data) {
            return null;
        }

        const obj = new Profile(data);
        obj.dob = data.dob;
        obj.bio = data.bio;
        obj.gender = data.gender;
        obj.address = Address.parse(data.address);
        obj.contacts = data.contacts;
        obj.workInfo = data.work_info;
        obj.meta = data.meta ?? {};

        if (data.avatar_photo) {
            obj.avatarPhoto = MediaObject.parse(data.avatar_photo);
        }

        if (data.cover_photo) {
            obj.coverPhoto = MediaObject.parse(data.cover_photo);
        }

        return obj;
    }

    getValueFromWorkInfo(key: string): any {
        return this.workInfo?.[key] ?? '';
    }

}

/** Model: Company */
export class Company extends ContactFunction {
    id: number;
    uuid: string;
    slug: string;
    title: string;
    description: string;
    website: string;
    type?: string;
    address?: Address | null;
    parent?: Company | null;
    project?: Project | null;  // Added for companies acting as sites
    logoPhoto?: MediaObject | null;
    coverPhoto?: MediaObject | null;
    industries: Industry[] = [];
    teamRoles: Team[] = [];
    appSettings: any;
    locale: any;
    meta: any;
    traceID: string;
    created?: Date;
    lastModified?: Date;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): Company | null {
        if (!data) {
            return null;
        }

        const obj = new Company(data);
        obj.id = data.id;
        obj.uuid = data.uuid;
        // if (data.uuid && data.uuid.length > 0) {
        //     obj.uuid = uuidParse(data.uuid);
        // }
        obj.title = data.title;
        obj.description = data.description;
        obj.slug = data.slug;
        obj.website = data.website;
        obj.type = data.type;
        obj.locale = data.locale;
        obj.meta = data.meta ?? {};
        obj.appSettings = data.app_settings ?? {};
        obj.contacts = data.contacts;
        obj.traceID = data.trace_id;
        obj.address = Address.parse(data.address);

        if (data.parent && data.parent?.id) {
            obj.parent = Company.parse(data.parent);
        } else {
            obj.parent = null;
        }

        if (data.project && data.project?.id) {
            obj.project = Project.parse(data.project);
        } else {
            obj.project = null;
        }

        if (data.logo_photo) {
            obj.logoPhoto = MediaObject.parse(data.logo_photo);
        }

        if (data.cover_photo) {
            obj.coverPhoto = MediaObject.parse(data.cover_photo);
        }

        if (data.roles) {
            obj.teamRoles = data.roles.map((x: any) => Team.parse(x)).filter(Boolean);
        }

        if (data.industries) {
            obj.industries = data.industries.map((x: any) => Industry.parse(x)).filter(Boolean);
        }

        if (data.created) {
            try {
                obj.created = new Date(data.created);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.last_modified) {
            try {
                obj.lastModified = new Date(data.last_modified);
            } catch (error) {
                // Invalid date format
            }
        }

        return obj;
    }

    getCTA(key: string): any {
        return this.meta?.cta?.[key] ?? '';
    }

    getIndustriesForTagForm(): { value: Industry; display: string }[] {
        return this.industries.map(x => ({
            value: x,
            display: x.title
        }));
    }

}

export class Team {
    id: number;
    role: string;
    meta: any;
    user?: User | null;
    company?: Company | null;
    hashtags: Tag[] = [];
    created?: Date;
    lastModified?: Date;

    static parse(data: any): Team | null {
        if (!data) {
            return null;
        }

        const obj = new Team();
        obj.id = data.id;
        obj.role = data.role;
        obj.meta = data.meta ?? {};

        if (data.user) {
            obj.user = User.parse(data.user);
        }

        if (data.company) {
            obj.company = Company.parse(data.company);
        }

        if (data.hashtags) {
            obj.hashtags = data.hashtags.map((x: any) => Tag.parse(x)).filter(Boolean);
        }

        if (data.created) {
            try {
                obj.created = new Date(data.created);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.last_modified) {
            try {
                obj.lastModified = new Date(data.last_modified);
            } catch (error) {
                // Invalid date format
            }
        }

        return obj;
    }

    getName(): string {
        if (this.user?.firstName || this.user?.lastName) {
            return `${this.user.firstName ?? ''} ${this.user.lastName ?? ''}`.trim();
        }
        return '';
    }

    getRoleLocale(): string {
        switch (this.role) {
            case 'OWN':
                return 's.teamRoles.owner';
            case 'STA':
                return 's.teamRoles.admin';
            case 'STF':
                return 's.teamRoles.staff';
            case 'SUV':
                return 's.teamRoles.supervisor';
            default:
                return '-';
        }
    }

    getGroupNames(): string {
        return this.hashtags.map(x => x.name).join(', ');
    }
}

/** Model: Media */
export class MediaObject extends RawJsonDict {
    id: number;
    title: string;
    description: string;
    mime: string;
    file: string;
    fileSize: number;
    user?: User | null;
    company?: Company | null;
    uploadBy?: User | null;
    meta: any;
    created?: Date;
    lastModified?: Date;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): MediaObject | null {
        if (!data) {
            return null;
        }

        const obj = new MediaObject(data);
        obj.id = data.id;
        obj.title = data.title;
        obj.description = data.description;
        obj.mime = data.mime_type;
        obj.file = data.file;
        obj.fileSize = data.file_size;
        obj.meta = data.meta ?? {};

        if (data.created) {
            try {
                obj.created = new Date(data.created);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.last_modified) {
            try {
                obj.lastModified = new Date(data.last_modified);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.company) {
            obj.company = Company.parse(data.company);
        }

        if (data.user) {
            obj.user = User.parse(data.user);
        }

        if (data.upload_by) {
            obj.uploadBy = User.parse(data.upload_by);
        }

        return obj;
    }

    isImage(): boolean {
        if (this.mime) {
            const imageType = ['jpeg', 'jpg', 'png', 'gif', 'bmp'];
            return imageType.includes(this.mime.split('/')[1]);
        }
        return false;
    }

    getFileData(): FileData {
        return {
            uploaded: true,
            loading: false,
            isImage: this.isImage(),
            previewUrl: this.file,
            name: this.title,
            alt: this.description,
            media: this,
            processing: false,
            status: 'ready',
            fileSize: this.fileSize
        };
    }

    getThumbnailObj(type: string): any | null {
        if (!this.isImage()) {
            return null;
        }

        return this.meta?.thumbnails?.[type] ?? null;
    }

    getThumbnailUrl(type: string): string | null {
        const obj = this.getThumbnailObj(type);
        return obj?.url ?? null;
    }
}

//file upload holder
export interface FileData {
    file?: File; // file object
    uploaded?: boolean;  // to indicate upload or not
    uploading?: boolean;
    uploadProgress?: number;
    loading?: boolean;
    isImage?: boolean;
    previewUrl?: any; // preview url or data
    name?: string; // name, most likely is file name
    alt?: string; // caption
    height?: number;
    width?: number;
    media?: MediaObject;
    thumbnailMediumFile?: ThumbnailData;
    thumbnailLargeFile?: ThumbnailData;
    processing?: boolean; //local resize or compressing
    status?: string; //pending, compress, thumbnail, upload,ready, complete
    tags?: string[];
    fileSize?: number;
    id?: any;
    index?: number;
    mime?: string;
    progressBar?: any;
}

export interface ThumbnailData {
    file?: File;
    url?: string;
    uploaded?: boolean;
    height?: number;
    width?: number;
    uploadName?: string;
}

/** Model: Project */
export class Project extends ContactFunction {
    id: number;
    uuid: string;
    slug: string;
    title: string;
    description: string;
    website: string;
    timezone: string;
    startTS: number;
    endTS: number;
    startDate?: Date;
    endDate?: Date;
    expirationTS: number;
    type: string;
    status: string;
    visibility: string;
    badgeFormats: any;
    appSettings: any;
    meta: any;
    statMeta: any;
    created?: Date;
    lastModified?: Date;
    address?: Address | null;
    app?: TheApp | null;
    logoPhoto?: MediaObject | null;
    coverPhoto?: MediaObject | null;
    owners: Company[] = [];
    industries: Industry[] = [];
    traceID: string;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): Project | null {
        if (!data) {
            return null;
        }

        const obj = new Project(data);
        obj.id = data.id;
        obj.uuid = data.uuid;

        obj.title = data.title;
        obj.description = data.description;
        obj.slug = data.slug;
        obj.website = data.website;
        obj.timezone = data.timezone;
        obj.startTS = data.start_ts;
        obj.endTS = data.end_ts;
        obj.expirationTS = data.expiration_ts;
        obj.type = data.type;
        obj.status = data.status;
        obj.visibility = data.visibility;
        obj.contacts = data.contacts;
        obj.traceID = data.trace_id;
        obj.badgeFormats = data.badge_formats;
        obj.appSettings = data.app_settings;
        obj.meta = data.meta ?? {};
        obj.statMeta = data.stat_meta ?? {};

        if (obj.startTS) {
            try {
                obj.startDate = new Date(obj.startTS * 1000);
            } catch (error) {
                // Invalid timestamp
            }
        }

        if (obj.endTS) {
            try {
                obj.endDate = new Date(obj.endTS * 1000);
            } catch (error) {
                // Invalid timestamp
            }
        }

        if (data.address) {
            obj.address = Address.parse(data.address);
        }

        if (data.app) {
            obj.app = TheApp.parse(data.app);
        }

        if (data.logo_photo) {
            obj.logoPhoto = MediaObject.parse(data.logo_photo);
        }

        if (data.cover_photo) {
            obj.coverPhoto = MediaObject.parse(data.cover_photo);
        }

        if (data.owners) {
            obj.owners = data.owners.map((x: any) => Company.parse(x)).filter(Boolean);
        }

        if (data.industries) {
            obj.industries = data.industries.map((x: any) => Industry.parse(x)).filter(Boolean);
        }

        if (data.created) {
            try {
                obj.created = new Date(data.created);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.last_modified) {
            try {
                obj.lastModified = new Date(data.last_modified);
            } catch (error) {
                // Invalid date format
            }
        }

        return obj;
    }

    getDisplayStatus(): string {
        switch (this.status) {
            case 'publish':
                return 'share.status.publish';
            case 'draft':
            default:
                return 'share.status.draft';
        }
    }

    getDisplayVisibility(): string {
        switch (this.visibility) {
            case 'private':
                return 'share.visibilityStatus.private';
            case 'public':
            default:
                return 'share.visibilityStatus.public';
        }
    }

    getDates(): Array<{ startTS: number; endTS: number; startDate: Date; endDate: Date }> {
        if (!this.startDate || !this.endDate) {
            return [];
        }

        const startDate = new Date(this.startDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(this.endDate);
        endDate.setHours(0, 0, 0, 0);
        endDate.setDate(endDate.getDate() + 1);

        const daySeconds = 86400000;
        let startTS = startDate.getTime();
        let endTS = startTS + daySeconds;
        const dates: Array<{ startTS: number; endTS: number; startDate: Date; endDate: Date }> = [];

        while (startTS < endDate.getTime()) {
            dates.push({
                startTS: startTS,
                endTS: endTS,
                startDate: new Date(startTS),
                endDate: new Date(endTS)
            });

            startTS = startTS + daySeconds;
            endTS = startTS + daySeconds;
        }

        return dates;
    }

    getOwner(): Company | null {
        return this.owners?.[0] ?? null;
    }

    getAppSetting(packageName: string = environment.appPackageName): any {
        return this.appSettings?.[packageName];
    }

    getAppSettingValue(key: string, packageName: string = environment.appPackageName): any {
        const appSetting = this.getAppSetting(packageName);
        return appSetting?.[key];
    }
}

/** Model: Site */
export class Site extends ContactFunction {
    id: number;
    title: string;
    description: string;
    expirationTS: number;
    meta: any;
    appSettings: any;
    traceID: string;
    created?: Date;
    lastModified?: Date;
    company?: Company | null;
    project?: Project | null;
    saved?: boolean;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): Site | null {
        if (!data) {
            return null;
        }

        const obj = new Site(data);
        obj.parseData(data);

        return obj;
    }

    parseData(data: any): void {
        this.id = data.id;
        this.title = data.title;
        this.description = data.description;
        this.expirationTS = data.expiration_ts;
        this.contacts = data.contacts;
        this.traceID = data.trace_id;
        this.meta = data.meta ?? {};
        this.appSettings = data.app_settings;

        if (data.company) {
            this.company = Company.parse(data.company);
        }

        if (data.project) {
            this.project = Project.parse(data.project);
        }

        if (data.created) {
            try {
                this.created = new Date(data.created);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.last_modified) {
            try {
                this.lastModified = new Date(data.last_modified);
            } catch (error) {
                // Invalid date format
            }
        }
    }

}

/** Model: Device */
export class Device extends RawJsonDict {
    id: number;
    uuid: string;
    serialNo: string;
    name: string;
    description: string;
    type: string;
    coverPhoto?: MediaObject | null;
    ownerType: string;
    app?: TheApp | null;
    project?: Project | null;
    site?: Site | null;
    attribute: any;
    meta: any;
    statMeta: any;
    created?: Date;
    lastModified?: Date;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): Device | null {
        if (!data) {
            return null;
        }

        const obj = new Device(data);
        obj.parseData(data);

        return obj;
    }

    parseData(data: any): void {
        this.id = data.id;
        this.uuid = data.uuid;
        this.name = data.name;
        this.serialNo = data.serial_no;
        this.description = data.description;
        this.type = data.type;
        this.ownerType = data.owner_type;
        this.attribute = data.attribute;
        this.meta = data.meta ?? {};
        this.statMeta = data.stat_meta ?? {};

        if (data.app) {
            this.app = TheApp.parse(data.app);
        }

        if (data.project) {
            this.project = Project.parse(data.project);
        }

        if (data.site) {
            this.site = Site.parse(data.site);
        }

        if (data.created) {
            try {
                this.created = new Date(data.created);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.last_modified) {
            try {
                this.lastModified = new Date(data.last_modified);
            } catch (error) {
                // Invalid date format
            }
        }
    }
}

/** Model: Catalog */
export class Catalog extends RawJsonDict {
    id: number;
    uuid: string;
    slug: string;
    title: string;
    description: string;
    summary: string;
    publishStatus: string;
    type: string;
    ownerType: string;
    coverPhoto?: MediaObject | null;
    galleries: MediaObject[] = [];
    attachments: MediaObject[] = [];
    parent?: Catalog | null;
    user?: User | null;
    company?: Company | null;
    site?: Site | null;
    hashtags: Tag[] = [];
    industries: Industry[] = [];
    locale: any;
    meta: any;
    created?: Date;
    lastModified?: Date;
    saved?: boolean;

    static parse(data: any): Catalog | null {
        if (!data) {
            return null;
        }

        const instance = new Catalog(data);
        instance.parseData(data);
        return instance;
    }

    parseData(data: any): void {
        this.id = data.id;
        this.uuid = data.uuid;
        this.slug = data.slug;
        this.title = data.title;
        this.description = data.description;
        this.summary = data.summary;
        this.publishStatus = data.publish_status;
        this.type = data.type;
        this.ownerType = data.owner_type ?? '';
        this.meta = data.meta ?? {};
        this.locale = data.locale ?? {};

        if (data.user) {
            this.user = User.parse(data.user);
        }

        if (data.company) {
            this.company = Company.parse(data.company);
        }

        if (data.site) {
            this.site = Site.parse(data.site);
        }

        if (data.cover_photo) {
            this.coverPhoto = MediaObject.parse(data.cover_photo);
        }

        if (data.galleries) {
            this.galleries = data.galleries.map((x: any) => MediaObject.parse(x)).filter(Boolean);
        }

        if (data.attachments) {
            this.attachments = data.attachments.map((x: any) => MediaObject.parse(x)).filter(Boolean);
        }

        if (data.parent) {
            this.parent = Catalog.parse(data.parent);
        }

        if (data.hashtags) {
            this.hashtags = data.hashtags.map((x: any) => Tag.parse(x)).filter(Boolean);
        }

        if (data.industries) {
            this.industries = data.industries.map((x: any) => Industry.parse(x)).filter(Boolean);
        }

        if (data.created) {
            try {
                this.created = new Date(data.created);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.last_modified) {
            try {
                this.lastModified = new Date(data.last_modified);
            } catch (error) {
                // Invalid date format
            }
        }
    }

    getPublishStatusName(): string {
        switch (this.publishStatus) {
            case 'publish':
                return 'share.status.published';
            default:
                return 'share.status.draft';
        }
    }
}

/** Model: Product */
export class Product extends RawJsonDict {
    id: number;
    uuid: string;
    name: string;
    description: string;
    company?: Company | null;
    coverPhoto?: MediaObject | null;
    media: MediaObject[] = [];
    attribute: any;
    meta: any;
    created?: Date;
    lastModified?: Date;

    static parse(data: any): Product | null {
        if (!data) {
            return null;
        }

        const instance = new Product(data);
        instance.parseData(data);
        return instance;
    }

    parseData(data: any): void {
        this.id = data.id;
        this.uuid = uuidv4(data.uuid);
        this.name = data.name;
        this.description = data.description;
        this.attribute = data.attribute ?? {};
        this.meta = data.meta ?? {};


        if (data.company) {
            this.company = Company.parse(data.company);
        }

        if (data.cover_photo) {
            this.coverPhoto = MediaObject.parse(data.cover_photo);
        } else {
            if (data.meta?.qrtrace?.cover_photo) {
                this.coverPhoto = MediaObject.parse(data.meta?.qrtrace?.cover_photo);
            }
        }

        if (data.media) {
            this.media = data.media.map((x: any) => MediaObject.parse(x)).filter(Boolean);
        }

        if (data.created) {
            try {
                this.created = new Date(data.created);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.last_modified) {
            try {
                this.lastModified = new Date(data.last_modified);
            } catch (error) {
                // Invalid date format
            }
        }
    }
}

/** Model: Report */
export class Report {
    id: number;
    uuid: string;
    uid: string;
    type: string;
    ownerType: string;
    site?: Site | null;
    project?: Project | null;
    app?: TheApp | null;
    meta: any;
    created?: Date;
    lastModified?: Date;

    static parse(data: any): Report | null {
        if (!data) {
            return null;
        }

        const instance = new Report();
        instance.id = data.id;
        instance.uuid = data.uuid;
        instance.uid = data.uid;
        instance.type = data.type;
        instance.ownerType = data.owner_type;
        instance.meta = data.meta ?? {};

        if (data.created) {
            try {
                instance.created = new Date(data.created);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.last_modified) {
            try {
                instance.lastModified = new Date(data.last_modified);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.site) {
            instance.site = Site.parse(data.site);
        }

        if (data.project) {
            instance.project = Project.parse(data.project);
        }

        if (data.app) {
            instance.app = TheApp.parse(data.app);
        }

        return instance;
    }
}

export class ReportWidget {
    id: number;
    uuid: string;
    uid: string;
    type: string;
    report?: Report | null;
    snapshot: any;
    startTs: number;
    endTs: number;
    meta: any;
    created?: Date;
    lastModified?: Date;

    static parse(data: any): ReportWidget | null {
        if (!data) {
            return null;
        }

        const instance = new ReportWidget();
        instance.id = data.id;
        instance.uuid = data.uuid;
        instance.uid = data.uid;
        instance.type = data.type;
        instance.startTs = data.start_ts;
        instance.endTs = data.end_ts;
        instance.snapshot = data.snapshot;
        instance.meta = data.meta ?? {};

        if (data.created) {
            try {
                instance.created = new Date(data.created);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.last_modified) {
            try {
                instance.lastModified = new Date(data.last_modified);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.report) {
            instance.report = Report.parse(data.report);
        }

        return instance;
    }
}

/** Model: CustomerBookmark */
export class CustomerBookmark {
    id: number;
    uuid: string;
    name: string;
    targetType: string;
    targetId: number;
    target: any;
    type: string;
    ownerType: string;
    app: number;
    company?: Company | null;
    site?: Company | null;
    attribute: any;
    created?: Date;
    lastModified?: Date;

    static parse(data: any): CustomerBookmark | null {
        if (!data) {
            return null;
        }

        const obj = new CustomerBookmark();
        obj.id = data.id;
        obj.uuid = data.uuid;
        obj.name = data.name;
        obj.targetType = data.target_type;
        obj.targetId = data.target_id;
        obj.target = data.target;
        obj.type = data.type;
        obj.ownerType = data.owner_type;
        obj.app = data.app;
        obj.attribute = data.attribute ?? {};
        if (data.site) {
            obj.site = Company.parse(data.site);
        }

        if (data.company) {
            obj.company = Company.parse(data.company);
        }

        if (data.created) {
            try {
                obj.created = new Date(data.created);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.last_modified) {
            try {
                obj.lastModified = new Date(data.last_modified);
            } catch (error) {
                // Invalid date format
            }
        }

        return obj;
    }
}

//menus
export interface JsonNavigationItem {
    id?: string;
    title?: string;
    subtitle?: string;
    type?: 'aside' | 'basic' | 'collapsable' | 'divider' | 'group' | 'spacer';
    hidden?: boolean;
    roles?: string[];
    link?: string;
    target?: '_blank' | '_self' | '_parent' | '_top' | string;
    icon?: string;
    children?: JsonNavigationItem[];
    meta?: any;
    lockedByCompany?: boolean;
}
